// @ts-nocheck
import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Search, Unlock } from 'lucide-react';
import { isServerBackedAnalysisId } from '../../../utils/isServerBackedAnalysisId';
import { clientDataPanelClass, clientSurfaceCardClass } from '../../../utils/clientChromeClasses';
import { Badge, Button } from '../components/WorkflowFormPrimitives';
import type { RunHistoryTableRow } from './runHistoryTableModel';

export function RunHistorySearchField({
  value,
  onChange,
  inputId = 'run-history-search',
  className = '',
}: {
  value: string;
  onChange: (next: string) => void;
  inputId?: string;
  /** e.g. `sm:flex-1 sm:min-w-0 sm:max-w-md` when merged in a toolbar row */
  className?: string;
}) {
  return (
    <div className={`relative w-full max-w-md ${className}`.trim()}>
      <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <input
        id={inputId}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search by analysis name, run date, run by, status…"
        className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
        aria-label="Search analysis run history"
      />
    </div>
  );
}

/**
 * Run history grid + snapshot actions — shared everywhere (client run history, firm company, firm dashboard).
 * Snapshot body is {@link RunSnapshotView} (full page or {@link RunSnapshotSlideOver}).
 */
export default function RunHistoryRunsTable({
  rows,
  onViewSnapshot,
  variant = 'standalone',
  hideAnalysisColumn = false,
  /** Client portal: analysis name links to `/client/:id` with same styling as Analyses list. */
  linkAnalysisNames = false,
  /** Client: keep sidebar analysis in sync when opening the name link (same as Analyses list). */
  onClientAnalysisNameClick,
  /** Shown under the name like Analyses (`First run by` / `Created on`); same for every row when scoped to one analysis. */
  analysisNameSubline = null,
  /** Client: show final/editable lock icon next to the name (matches Analyses). */
  showAnalysisNameLock = false,
  analysisNameLocked = false,
  searchToolbar,
}: {
  rows: RunHistoryTableRow[];
  onViewSnapshot: (row: RunHistoryTableRow) => void;
  variant?: 'standalone' | 'embedded';
  hideAnalysisColumn?: boolean;
  linkAnalysisNames?: boolean;
  onClientAnalysisNameClick?: (analysisId: string) => void;
  analysisNameSubline?: { createdBy?: string; createdOn?: string } | null;
  showAnalysisNameLock?: boolean;
  analysisNameLocked?: boolean;
  /** Merges search into the top of the card above the table header (standalone layout). */
  searchToolbar?: {
    value: string;
    onChange: (next: string) => void;
    inputId?: string;
  };
}) {
  const wrapClass = variant === 'embedded' ? 'min-w-0 overflow-x-auto' : `${clientSurfaceCardClass} overflow-x-auto`;
  const tableMin = 'min-w-[560px]';
  const colSpan = hideAnalysisColumn ? 5 : 6;

  const table = (
    <table className={`w-full ${tableMin}`}>
      <thead>
        <tr className="bg-gray-50 border-b border-gray-200">
          {hideAnalysisColumn ? null : (
            <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Analysis Name
            </th>
          )}
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Run Date and Run By
          </th>
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Individuals
          </th>
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Over Count</th>
          <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Snapshot</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.length === 0 ? (
          <tr>
            <td colSpan={colSpan} className="px-5 py-8 text-center text-sm text-gray-500">
              No analysis run history matches your search.
            </td>
          </tr>
        ) : (
          rows.map((r) => {
            const statusLower = String(r.status ?? '')
              .trim()
              .toLowerCase();
            const isOver = statusLower === 'over_threshold' || statusLower === 'over';
            const badgeVariant = isOver ? 'danger' : 'success';
            const statusLabel = isOver ? 'Over Threshold' : 'OK';
            const aid = String(r.analysisId ?? '').trim();
            const rid = String(r.runId ?? '').trim();
            const canViewSnapshot =
              r.hasSnapshot === true && isServerBackedAnalysisId(aid) && isServerBackedAnalysisId(rid);
            const analysisNameLabel = String(r.analysisName ?? '').trim() || 'Untitled analysis';
            const sublineFromAnalysis =
              analysisNameSubline && (analysisNameSubline.createdBy || analysisNameSubline.createdOn) ? (
                <div className="text-xs text-gray-500">
                  {analysisNameSubline.createdBy ? `First run by ${analysisNameSubline.createdBy}` : null}
                  {analysisNameSubline.createdBy && analysisNameSubline.createdOn ? ' • ' : null}
                  {analysisNameSubline.createdOn ? `Created on ${analysisNameSubline.createdOn}` : null}
                </div>
              ) : null;
            const nameSubline = sublineFromAnalysis;
            const runByDisplay = String(r.ranByName || r.ranByEmail || '').trim() || '—';
            const runDateDisplay = (() => {
              const d = new Date(r.runDate ?? '');
              if (Number.isNaN(d.getTime())) return r.runDate;
              return d.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: 'numeric',
                minute: '2-digit',
              });
            })();

            return (
              <tr key={r.rowKey} className="group hover:bg-gray-50/50">
                {hideAnalysisColumn ? null : (
                  <td className="px-5 py-4">
                    <div className="space-y-0.5">
                      <div className="inline-flex items-center gap-2">
                        {linkAnalysisNames && isServerBackedAnalysisId(aid) ? (
                          <Link
                            to={`/client/${encodeURIComponent(aid)}`}
                            onClick={() => onClientAnalysisNameClick?.(aid)}
                            className="font-medium text-emerald-700 hover:text-emerald-900 hover:underline"
                          >
                            {analysisNameLabel}
                          </Link>
                        ) : (
                          <span className="text-sm font-medium text-gray-900">{analysisNameLabel}</span>
                        )}
                        {showAnalysisNameLock && linkAnalysisNames ? (
                          <span
                            className={`inline-flex items-center ${analysisNameLocked ? 'text-red-600' : 'text-slate-500'}`}
                            title={
                              analysisNameLocked
                                ? 'Final: workflow edits and new runs are disabled.'
                                : 'Editable: analysis is not final.'
                            }
                            aria-label={analysisNameLocked ? 'Final analysis' : 'Editable analysis'}
                          >
                            {analysisNameLocked ? (
                              <Lock size={15} className="shrink-0" strokeWidth={2.25} aria-hidden />
                            ) : (
                              <Unlock size={15} className="shrink-0" strokeWidth={2} aria-hidden />
                            )}
                          </span>
                        ) : null}
                      </div>
                      {nameSubline}
                    </div>
                  </td>
                )}
                <td className="px-5 py-4">
                  <div className="space-y-0.5">
                    <div className="text-sm text-gray-600 tabular-nums">{runDateDisplay}</div>
                    <div className="text-xs text-gray-500">Run by {runByDisplay}</div>
                  </div>
                </td>
                <td className="px-5 py-4">
                  <Badge size="table" variant={badgeVariant}>
                    {statusLabel}
                  </Badge>
                </td>
                <td className="px-5 py-4 text-sm text-gray-600 tabular-nums">{r.individualsCount}</td>
                <td className="px-5 py-4 text-sm text-gray-600 tabular-nums">{r.overCount}</td>
                <td className="px-5 py-4 text-sm text-gray-600">
                  {canViewSnapshot ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      className="inline-flex items-center gap-1.5"
                      onClick={() => onViewSnapshot(r)}
                    >
                      View
                    </Button>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  if (searchToolbar && variant === 'standalone') {
    return (
      <div className={clientDataPanelClass}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-5 py-3 border-b border-gray-200 bg-gray-50/80">
          <RunHistorySearchField
            value={searchToolbar.value}
            onChange={searchToolbar.onChange}
            inputId={searchToolbar.inputId}
            className="sm:flex-1 sm:min-w-0 sm:max-w-md"
          />
        </div>
        <div className="overflow-x-auto">{table}</div>
      </div>
    );
  }

  return <div className={wrapClass}>{table}</div>;
}
